import React from 'react';
import { motion } from 'framer-motion';
import { vars } from '../../theme';
import styled from 'styled-components';

export interface HBHotspotProps {
    /** Controls visibility; when false the component renders nothing. */
    isShown?: boolean;
    /** Controls the dot and pulse animation sizing: 'default' renders a 10px dot, 'small' renders a 4px dot. */
    variant: 'default' | 'small';
    /** CSS position value applied to the hotspot container. Use 'absolute' to overlay a parent element. */
    position?: React.CSSProperties['position'];
    /** Distance from the top edge of the positioned ancestor. Use with `position: 'absolute'`. */
    top?: React.CSSProperties['top'];
    /** Distance from the right edge of the positioned ancestor. Use with `position: 'absolute'`. */
    right?: React.CSSProperties['right'];
    /** Distance from the bottom edge of the positioned ancestor. Use with `position: 'absolute'`. */
    bottom?: React.CSSProperties['bottom'];
    /** Distance from the left edge of the positioned ancestor. Use with `position: 'absolute'`. */
    left?: React.CSSProperties['left'];
}

const hotspotSize: Record<
    HBHotspotProps['variant'],
    { padding: number; size: number; effectSize: number; borderSize: number }
> = {
    default: { padding: 4, size: 10, effectSize: 20, borderSize: 2 },
    small: { padding: 2, size: 4, effectSize: 10, borderSize: 1 }
};

const HBHotspot = ({ isShown, variant, ...position }: HBHotspotProps) => {
    if (!isShown) {
        return null;
    }

    const { padding, size, effectSize, borderSize } = hotspotSize[variant];

    return (
        <HotspotPeripheral
            data-testid="hotspot"
            $padding={padding}
            style={{ ...position }}
        >
            <Hotspot $size={size}>
                <HotspotEffect
                    $borderSize={borderSize}
                    $effectSize={effectSize}
                />
            </Hotspot>
        </HotspotPeripheral>
    );
};

const HotspotPeripheral = styled.div<{ $padding: number }>`
    position: relative;
    border-radius: ${vars.radius.full};
    background: transparent;
    padding: ${props => props.$padding}px;
    z-index: 2;
`;

const Hotspot = styled.div<{ $size: number }>`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    background: ${vars.colors.base.purple};
    border-radius: ${vars.radius.full};
`;

const HotspotEffect = styled(motion.div).attrs({
    animate: {
        opacity: [1, 0.6, 0],
        scale: [0.5, 1.5]
    },
    transition: { duration: 1.5, ease: 'easeOut', repeat: Infinity }
})<{ $effectSize: number; $borderSize: number }>`
    position: absolute;
    height: ${props => props.$effectSize}px;
    width: ${props => props.$effectSize}px;
    border-radius: ${vars.radius.full};
    border: ${props => props.$borderSize}px solid ${vars.colors.base.purple};
`;

export default HBHotspot;
